import React from 'react';
import { ArrowLeft, Check, Zap, Shield, Clock, BarChart3, Globe, Code } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PricingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#12121a] to-[#0a0a0f]">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white/70" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{background: 'linear-gradient(135deg, #F96F51, #ff8a6b)'}}>
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-white">SyncPulse</h1>
                <p className="text-xs text-white/50">Pricing Guide</p>
              </div>
            </div>
          </div>
          <div className="px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/30">
            <span className="text-xs font-medium text-amber-400">CONFIDENTIAL</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-16">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
            Transform Your Music Catalog
            <br />
            <span style={{color: '#F96F51'}}>In Minutes, Not Months</span>
          </h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            AI-powered metadata generation and sync descriptions for the modern music industry
          </p>
        </div>

        {/* What You Get */}
        <div className="glass-card rounded-2xl p-8 mb-12 border border-white/10">
          <h2 className="font-heading text-2xl font-bold text-white mb-6">What You Get Per Track</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: '🎤', title: 'Lyrics Transcription', desc: 'Accurate lyrics from any audio' },
              { icon: '🏷️', title: '30+ Metadata Fields', desc: 'Genre, mood, tempo, key, instruments' },
              { icon: '✍️', title: 'AI Sync Description', desc: 'Compelling, licensable descriptions' },
              { icon: '📤', title: 'Export Ready', desc: 'CSV & JSON for your systems' },
            ].map((item, i) => (
              <div key={i} className="text-center p-4">
                <div className="text-4xl mb-3">{item.icon}</div>
                <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-sm text-white/50">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* The Problem We Solve */}
        <div className="glass-card rounded-2xl p-8 mb-12 border border-white/10">
          <h2 className="font-heading text-2xl font-bold text-white mb-6">The Problem We Solve</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-white/50 font-medium">Traditional Process</th>
                  <th className="text-center py-3 px-4 text-white/50 font-medium">Time</th>
                  <th className="text-center py-3 px-4 text-white/50 font-medium">Cost</th>
                </tr>
              </thead>
              <tbody className="text-white/80">
                <tr className="border-b border-white/5">
                  <td className="py-3 px-4">Manual lyrics transcription</td>
                  <td className="py-3 px-4 text-center">10-15 min/track</td>
                  <td className="py-3 px-4 text-center">$2-5/track</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-4">Professional metadata tagging</td>
                  <td className="py-3 px-4 text-center">10-20 min/track</td>
                  <td className="py-3 px-4 text-center">$5-20/track</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-4">Sync description writing</td>
                  <td className="py-3 px-4 text-center">20-30 min/track</td>
                  <td className="py-3 px-4 text-center">$15-50/track</td>
                </tr>
                <tr className="font-bold" style={{color: '#F96F51'}}>
                  <td className="py-3 px-4">Total Manual Work</td>
                  <td className="py-3 px-4 text-center">40-65 min/track</td>
                  <td className="py-3 px-4 text-center">$22-75/track</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-green-500/5 border border-green-500/20">
            <p className="text-green-400 font-semibold text-center">
              SyncPulse delivers all of this in under 2 minutes per track.
            </p>
          </div>
        </div>

        {/* B2C Pricing */}
        <div className="mb-12">
          <h2 className="font-heading text-2xl font-bold text-white mb-2">B2C Pricing</h2>
          <p className="text-white/50 mb-6">For Artists, Producers & Small Labels</p>
          
          <div className="grid md:grid-cols-5 gap-4">
            {[
              { name: 'Trial', tracks: '3', price: 'FREE', perTrack: '-', highlight: false },
              { name: 'Starter', tracks: '10', price: '$15', perTrack: '$1.50', highlight: false },
              { name: 'Pro', tracks: '50', price: '$49', perTrack: '$0.98', highlight: true },
              { name: 'Studio', tracks: '200', price: '$149', perTrack: '$0.75', highlight: false },
              { name: 'Unlimited', tracks: '∞/mo', price: '$199/mo', perTrack: '-', highlight: false },
            ].map((plan, i) => (
              <div 
                key={i} 
                className={`rounded-2xl p-6 border ${
                  plan.highlight 
                    ? 'border-2 bg-gradient-to-b from-[#F96F51]/20 to-transparent' 
                    : 'border-white/10 bg-white/5'
                }`}
                style={plan.highlight ? {borderColor: '#F96F51'} : {}}
              >
                {plan.highlight && (
                  <div className="text-xs font-bold text-center mb-2" style={{color: '#F96F51'}}>POPULAR</div>
                )}
                <h3 className="font-heading text-lg font-bold text-white mb-1">{plan.name}</h3>
                <div className="text-3xl font-bold text-white mb-1">{plan.price}</div>
                <div className="text-sm text-white/50 mb-4">{plan.tracks} tracks</div>
                {plan.perTrack !== '-' && (
                  <div className="text-xs text-white/40">{plan.perTrack}/track</div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 glass-card rounded-xl p-6 border border-white/10">
            <h4 className="font-semibold text-white mb-3">What's Included (All Plans):</h4>
            <div className="grid md:grid-cols-3 gap-3">
              {[
                'Full lyrics transcription',
                'Complete metadata tagging (30+ fields)',
                'AI-generated sync descriptions',
                'CSV & JSON export',
                'Edit & refine results',
                'Secure cloud storage',
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-2 text-white/70">
                  <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* B2B Pricing */}
        <div className="mb-12">
          <h2 className="font-heading text-2xl font-bold text-white mb-2">B2B Pricing</h2>
          <p className="text-white/50 mb-6">For Sync Agencies, Labels, Distributors & Music Libraries</p>
          
          <div className="glass-card rounded-2xl border border-white/10 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10" style={{background: 'rgba(249,111,81,0.1)'}}>
                  <th className="text-left py-4 px-6 text-white font-semibold">Annual Volume</th>
                  <th className="text-center py-4 px-6 text-white font-semibold">Per Track</th>
                  <th className="text-right py-4 px-6 text-white font-semibold">Example Annual Cost</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { volume: 'Up to 1,000', perTrack: '$0.75', example: '$750' },
                  { volume: '1,001 - 5,000', perTrack: '$0.50', example: '$2,500' },
                  { volume: '5,001 - 25,000', perTrack: '$0.35', example: '$8,750' },
                  { volume: '25,001 - 100,000', perTrack: '$0.25', example: '$25,000' },
                  { volume: '100,001 - 500,000', perTrack: '$0.20', example: '$100,000' },
                  { volume: '500,001+', perTrack: 'Custom', example: 'Contact Us' },
                ].map((tier, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-4 px-6 text-white/80">{tier.volume}</td>
                    <td className="py-4 px-6 text-center font-semibold" style={{color: '#F96F51'}}>{tier.perTrack}</td>
                    <td className="py-4 px-6 text-right text-white/60">{tier.example}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 glass-card rounded-xl p-6 border border-white/10">
            <h4 className="font-semibold text-white mb-3">Enterprise Features:</h4>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { icon: Shield, label: 'Dedicated Account Manager' },
                { icon: Code, label: 'API Access' },
                { icon: Globe, label: 'White-label Options' },
                { icon: Zap, label: 'Priority Processing' },
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-3 text-white/70">
                  <feature.icon className="w-5 h-5 text-white/40" />
                  <span className="text-sm">{feature.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ROI Calculator */}
        <div className="glass-card rounded-2xl p-8 mb-12 border border-white/10">
          <h2 className="font-heading text-2xl font-bold text-white mb-6">ROI Calculator</h2>
          <p className="text-white/50 mb-6">Your Savings with SyncPulse</p>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-white/50 font-medium">Catalog Size</th>
                  <th className="text-center py-3 px-4 text-white/50 font-medium">Manual Cost</th>
                  <th className="text-center py-3 px-4 text-white/50 font-medium">SyncPulse Cost</th>
                  <th className="text-center py-3 px-4 text-white/50 font-medium">You Save</th>
                  <th className="text-center py-3 px-4 text-white/50 font-medium">Time Saved</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { size: '100 tracks', manual: '$3,000', sync: '$98', save: '$2,902 (97%)', time: '65 hours' },
                  { size: '1,000 tracks', manual: '$30,000', sync: '$750', save: '$29,250 (98%)', time: '650 hours' },
                  { size: '10,000 tracks', manual: '$300,000', sync: '$5,000', save: '$295,000 (98%)', time: '6,500 hours' },
                  { size: '100,000 tracks', manual: '$3,000,000', sync: '$25,000', save: '$2,975,000 (99%)', time: '65,000 hours' },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-4 px-4 text-white font-medium">{row.size}</td>
                    <td className="py-4 px-4 text-center text-red-400">{row.manual}</td>
                    <td className="py-4 px-4 text-center" style={{color: '#F96F51'}}>{row.sync}</td>
                    <td className="py-4 px-4 text-center text-green-400 font-bold">{row.save}</td>
                    <td className="py-4 px-4 text-center text-white/60">{row.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-white/40 mt-4 text-center">
            *Based on average manual costs of $30/track and 40 minutes processing time
          </p>
        </div>

        {/* Why SyncPulse */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          {[
            { icon: Clock, title: 'Speed', points: ['2 minutes per track', 'Batch processing', 'No waiting'] },
            { icon: BarChart3, title: 'Quality', points: ['OpenAI Whisper', 'Cyanite analysis', 'GPT-5.2 descriptions'] },
            { icon: Shield, title: 'Consistency', points: ['Uniform metadata', 'Standard taxonomy', 'Professional tone'] },
            { icon: Code, title: 'Integration', points: ['REST API', 'Webhooks', 'CSV/JSON exports'] },
          ].map((item, i) => (
            <div key={i} className="glass-card rounded-xl p-6 border border-white/10">
              <item.icon className="w-8 h-8 mb-4" style={{color: '#F96F51'}} />
              <h3 className="font-heading text-lg font-bold text-white mb-3">{item.title}</h3>
              <ul className="space-y-2">
                {item.points.map((point, j) => (
                  <li key={j} className="text-sm text-white/60 flex items-center gap-2">
                    <Check className="w-3 h-3 text-green-400" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center glass-card rounded-2xl p-12 border border-white/10" style={{background: 'linear-gradient(135deg, rgba(249,111,81,0.1), rgba(249,111,81,0.05))'}}>
          <h2 className="font-heading text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Contact us for a demo or to discuss enterprise pricing for your catalog.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:sales@syncit.ac" 
              className="px-8 py-4 btn-primary font-semibold"
            >
              Contact Sales
            </a>
            <a 
              href="https://syncpulse.syncit.ac" 
              className="px-8 py-4 rounded-xl border border-white/20 text-white hover:bg-white/10 transition-colors font-semibold"
            >
              Try Free (3 Tracks)
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-white/40 text-sm">
          <p>Pricing effective as of 2026. All prices in USD.</p>
          <p className="mt-1">Volume discounts applied automatically. Enterprise pricing requires annual commitment.</p>
          <p className="mt-4 text-xs">CONFIDENTIAL - This document is intended for authorized recipients only.</p>
        </div>
      </main>
    </div>
  );
}
